import { DateTime } from 'luxon';
import { PREFERENCE_WINDOWS } from '../../constants';

/**
 * best-match scoring algorithm for follow-up series.
 *
 * Priority tiers (higher score = better match):
 *   4 — Preferred + recurring (matches patient time preferences AND has high week coverage)
 *   3 — Preferred (matches patient time preferences)
 *   2 — Recurring (high week coverage relative to total bookable weeks)
 *   1 — Near preferred (within 1 hour of a preferred window boundary)
 *   0 — First available (fallback)
 *
 * Within the same tier, higher weekCoverage wins.
 */

const NEAR_PREFERRED_TOLERANCE_HOURS = 1;

const HIGH_COVERAGE_THRESHOLD = 0.6;

interface PreferenceCheckInput {
  opening: FollowUpOpening;
  preferences: PatientAvailabilityPreferences | null;
  timezone?: string;
  tolerance: number; // 0 for exact match, NEAR_PREFERRED_TOLERANCE_HOURS for lenient
}

const isPreferredTime = ({
  opening,
  preferences,
  timezone,
  tolerance = 0,
}: PreferenceCheckInput): boolean => {
  if (!preferences?.length) return false;
  const dt = DateTime.fromISO(opening.startTime, { zone: timezone || 'utc' });
  const hour = dt.hour;
  return preferences.some((pref) => {
    const window = PREFERENCE_WINDOWS[pref];
    if (!window) return false;
    const lowerBound = window.startHour - tolerance;
    const upperBound = window.endHour + tolerance;
    return hour >= lowerBound && hour < upperBound;
  });
};

const isNearPreferredTime = (in_: Omit<PreferenceCheckInput, 'tolerance'>): boolean =>
  isPreferredTime({ ...in_, tolerance: NEAR_PREFERRED_TOLERANCE_HOURS });

export const scoreSeries = (
  series: FollowUpSeries,
  totalBookableWeeks: number,
  preferences: PatientAvailabilityPreferences | null,
  timezone?: string,
): number => {
  const isHighCoverage =
    totalBookableWeeks > 0 &&
    series.weekCoverage / totalBookableWeeks >= HIGH_COVERAGE_THRESHOLD;

  // Sample the first opening to determine time preference match
  const sampleOpening = series.openingsByWeek.values().next().value;
  if (!sampleOpening) return 0;

  const matchesPreferred = isPreferredTime({ opening: sampleOpening, preferences, timezone, tolerance: 0 });
  const matchesNearPreferred = isNearPreferredTime({ opening: sampleOpening, preferences, timezone });

  if (matchesPreferred && isHighCoverage) return 4;
  if (matchesPreferred) return 3;
  if (isHighCoverage) return 2;
  if (matchesNearPreferred) return 1;
  return 0;
};

/**
 * Selects the best series from the available options using best match algorithm.
 * Returns the best series or undefined if no series are available.
 */
export const selectBestMatch = (
  series: FollowUpSeries[],
  totalBookableWeeks: number,
  preferences: PatientAvailabilityPreferences | null,
  timezone?: string,
): FollowUpSeries | undefined => {
  if (!series.length) return undefined;

  let bestSeries = series[0];
  let bestScore = scoreSeries(bestSeries, totalBookableWeeks, preferences, timezone);

  for (let i = 1; i < series.length; i++) {
    const score = scoreSeries(series[i], totalBookableWeeks, preferences, timezone);
    if (
      score > bestScore ||
      (score === bestScore && series[i].weekCoverage > bestSeries.weekCoverage)
    ) {
      bestScore = score;
      bestSeries = series[i];
    }
  }

  return bestSeries;
};
